import i18n from 'i18next';
import intervalPlural from 'i18next-intervalplural-postprocessor';
import { initReactI18next } from 'react-i18next';
import numeral from 'numeral';
import merge from 'lodash/merge';
import LanguageDetector from 'i18next-browser-languagedetector';
import {
	DEFAULT_LOCALE,
	getSupportedLanguageCodes,
	getSupportedLocaleOptions,
	resolveFallbackChain,
} from './localeConfig';

const localeOptions = getSupportedLocaleOptions();

const loadLocaleResource = (locale) => {
	// eslint-disable-next-line global-require, import/no-dynamic-require
	const resource = require(`./${locale}.json`);

	return resource.default || resource;
};

const fileResources = localeOptions.reduce(
	(resources, { locale }) => ({
		...resources,
		[locale]: loadLocaleResource(locale),
	}),
	{}
);

const buildLocaleResource = ({ locale, language }) =>
	merge({}, fileResources[language] || {}, fileResources[locale] || {});

function addTranslationResources(
	resources,
	namespace,
	deep = true,
	overwrite = true
) {
	if (!namespace) {
		/* eslint-disable-next-line no-console */
		console.error('You must provide a namespace to the addResources function.');
	}

	Object.entries(resources).forEach(([language, translations]) =>
		i18n.addResourceBundle(language, namespace, translations, deep, overwrite)
	);
}

function addLanguageResource(language, resource) {
	if (!resource) {
		return;
	}

	Object.entries(resource).forEach(([namespace, translations]) => {
		i18n.addResourceBundle(language, namespace, translations, true, true);
	});
}

function addLocaleResource(localeOption) {
	const resource = buildLocaleResource(localeOption);

	Object.entries(resource).forEach(([namespace, translations]) => {
		addTranslationResources({ [localeOption.locale]: translations }, namespace);
	});
}

i18n
	// detect user language
	// learn more: https://github.com/i18next/i18next-browser-languageDetector
	.use(LanguageDetector)
	// pass the i18n instance to react-i18next.
	.use(initReactI18next)
	// init i18next
	// for all options read: https://www.i18next.com/overview/configuration-options

	.use(intervalPlural)
	.init({
		fallbackLng: (code) => resolveFallbackChain(code).slice(1),
		supportedLngs: [
			...localeOptions.map(({ locale }) => locale),
			...getSupportedLanguageCodes(),
		],
		lng: DEFAULT_LOCALE,
		defaultNS: 'common',
		fallbackNS: 'common',
		// debug: process.env.NODE_ENV === 'development',
		interpolation: {
			escapeValue: false, // not needed for react as it escapes by default
			format: (value, format) => {
				if (format === 'number') {
					return numeral(value).format('0,0');
				}

				return null;
			},
		},
		resources: {},
	});

getSupportedLanguageCodes().forEach((language) => {
	const localeOption = localeOptions.find((option) => option.language === language);
	addLanguageResource(language, localeOption && buildLocaleResource(localeOption));
});

localeOptions.forEach((option) => {
	addLocaleResource(option);
});

export default i18n;
